import React, { PureComponent } from 'react';
import { Modal, DatePicker, Col, message } from 'antd';
import moment from 'moment';
import get from 'lodash/get';
import { getCountry } from '@fxtr/i18n';
import config from '$config';
import { api } from '$app/api';

import { EditButton, DetailsCard, DateTimeSpan, Address } from './styled';
import Title from '../../../components/Title';
import { AddressSelectorWithGooglePlaces } from '$app/shared/components/AddressSelectorWithGooglePlaces';
import PostcodeLookup from '$app/shared/components/PostcodeLookup';
import { shouldUseUkPostcodeLookup, prepareAddressForSave } from './addressLookup';

import RescheduleTriggerModal from './components/RescheduleTriggerModal';
import RescheduleProposalAlert from './components/RescheduleProposalAlert';

import { shouldShowRescheduleSection } from './components/RescheduleProposalAlert/logic';

const STATE_UNALLOCATED = 10000;
const STATE_PENDING = 10100;
const STATE_ALLOCATED = 10200;
const STATE_AWAITING_PICKUP = 10300;

const cancelTripsFromHour = get(config, 'booking.cancelTripFromHour', 12);
const cancelTripsFromDays = get(config, 'booking.cancelTripsFromDays', 1);

const dateTimeFormat = 'YYYY-MM-DDTHH:mm';
const dateTimeFormatPlaceHolder = 'YYYY-MM-DD HH:mm';

const getAddressDisplay = (address) =>
  `${[address.line1, address.line2, address.line3, address.line4].join(' ').trim()}, ${address.city}`;

const displayAddress = (address) => {
  if (!address) return null;
  if (!address.line1 && !address.city && !address.postcode) return null;
  return (
    <Address>
      {`${address.line1}, `}
      {address.line2 && `${address.line2}, `}
      {address.line3 && `${address.line3}, `}
      {address.line4 && `${address.line4}, `} <br />
      {address.city}, <br />
      {address.postcode}
    </Address>
  );
};

const displayedGarageAddress = (garage) => {
  if (!garage) return 'No garage selected';

  const address = {
    line1: garage.addressLine1,
    line2: garage.addressLine2,
    line3: garage.addressLine3,
    city: garage.city,
    postcode: garage.postcode,
  };

  return displayAddress(address);
};

class BookingDetails extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      editingCollectionTime: false,
      confirmingCollectionTime: false,

      editingDeliveryTime: false,
      confirmingDeliveryTime: false,

      editingCollectionAddress: false,
      confirmingCollectionAddress: false,

      editingDeliveryAddress: false,
      confirmingDeliveryAddress: false,

      confirmLoading: false,
      collectionData: {
        time: props.booking.collectionDateTimeLocal,
        newTime: null,
        newAddress: null,
      },
      deliveryData: {
        time: props.booking.deliveryDateTimeLocal,
        newTime: null,
        newAddress: null,
      },
    };
  }

  resetEditingStates = () =>
    this.setState({
      editingCollectionTime: false,
      editingDeliveryTime: false,
      editingCollectionAddress: false,
      editingDeliveryAddress: false,
      collectionData: {
        newTime: null,
        newAddress: null,
      },
      deliveryData: {
        newTime: null,
        newAddress: null,
      },
    });

  resetConfirmingStates = () =>
    this.setState({
      confirmLoading: false,
      confirmingCollectionTime: false,
      confirmingDeliveryTime: false,
      confirmingCollectionAddress: false,
      confirmingDeliveryAddress: false,
    });

  handleCancel = () => {
    this.resetEditingStates();
    this.resetConfirmingStates();
  };

  setEditingStates = (edit) => {
    switch (edit) {
      case 'collection-time': {
        return this.state.editingCollectionTime
          ? this.resetEditingStates()
          : (this.resetEditingStates(), this.setState({ editingCollectionTime: true }));
      }
      case 'delivery-time': {
        return this.state.editingDeliveryTime
          ? this.resetEditingStates()
          : (this.resetEditingStates(), this.setState({ editingDeliveryTime: true }));
      }
      case 'collection-address': {
        return this.state.editingCollectionAddress
          ? this.resetEditingStates()
          : (this.resetEditingStates(), this.setState({ editingCollectionAddress: true }));
      }
      case 'delivery-address': {
        return this.state.editingDeliveryAddress
          ? this.resetEditingStates()
          : (this.resetEditingStates(), this.setState({ editingDeliveryAddress: true }));
      }
      default: {
        this.resetEditingStates();
        this.resetConfirmingStates();
      }
    }
  };

  editToggleSwitch = (edit) => (
    <EditButton onClick={() => this.setEditingStates(edit)}>
      <i className="icon-pencil-write-2" />
    </EditButton>
  );

  isCollectionEditable = () => {
    const states = new Set(['UNALLOCATED', 'PENDING', 'ALLOCATED', 'AWAITING_PICKUP', 'GARAGE_DECLINED']);
    const { booking } = this.props || {};
    const currentStatus = get(booking, 'masterStatus.masterStatusName', null);

    const finaliseRescheduleSectionNotVisible = !shouldShowRescheduleSection(booking?.garageInvites);
    return states.has(currentStatus) && finaliseRescheduleSectionNotVisible;
  };

  isDeliveryEditable = () => {
    const states = new Set([
      'UNALLOCATED',
      'PENDING',
      'ALLOCATED',
      'AWAITING_PICKUP',
      'BEING_COLLECTED',
      'TRANSPORT_TO_GARAGE',
      'GARAGE_DECLINED',
      'AT_GARAGE',
      'WORK_COMPLETE',
      'PAID',
    ]);
    const { booking } = this.props;
    const currentStatus = get(booking, 'masterStatus.masterStatusName', null);
    const finaliseRescheduleSectionNotVisible = !shouldShowRescheduleSection(booking.garageInvites);
    return states.has(currentStatus) && finaliseRescheduleSectionNotVisible;
  };

  /**
   * EDIT COLLECTION/DELIVERY DATE-TIME
   */

  renderAddress = (address) => (
    <>
      <span>
        {address.line1}, {address.city}
      </span>
      <br />
      <span>{address.county}</span>
      <br />
      <span>{address.postcode}</span>
    </>
  );

  bookingDayChanged = () => {
    const { collectionData, deliveryData } = this.state;
    const { collectionDateTimeLocal, deliveryDateTimeLocal } = this.props.booking;
    if (collectionData.newTime)
      return moment(collectionData.newTime).dayOfYear() !== moment(collectionDateTimeLocal).dayOfYear();
    if (deliveryData.newTime)
      return moment(deliveryData.newTime).dayOfYear() !== moment(deliveryDateTimeLocal).dayOfYear();
  };

  triggerTimeForCancelingFixterHeroesTrips = () => {
    const { collectionDateTimeLocal } = this.props.booking;
    return moment(collectionDateTimeLocal)
      .subtract(cancelTripsFromDays, 'day')
      .startOf('day')
      .add(cancelTripsFromHour, 'h');
  };

  keepTripsForFixterHero = () => moment().isAfter(this.triggerTimeForCancelingFixterHeroesTrips);

  nextStatusForAwaitingPickup = (type) => {
    if (this.keepTripsForFixterHero() && !this.bookingDayChanged()) return STATE_AWAITING_PICKUP;
    return type === 'collection' && this.bookingDayChanged() ? STATE_UNALLOCATED : STATE_ALLOCATED;
  };

  isNotYetAtGarage = () => {
    const { masterStatus } = this.props.booking;
    const availableStates = ['AT_GARAGE', 'WORK_COMPLETE', 'PAID'];
    return availableStates.indexOf(masterStatus.masterStatusName) === -1;
  };

  getNextState = (type) => {
    const { masterStatus } = this.props.booking;
    const nextStatusAfterChange = {
      PENDING: () => (type === 'collection' && this.bookingDayChanged() ? STATE_UNALLOCATED : STATE_PENDING),
      ALLOCATED: () =>
        type === 'collection' && this.bookingDayChanged() ? STATE_UNALLOCATED : STATE_ALLOCATED,
      AWAITING_PICKUP: () => this.nextStatusForAwaitingPickup(type),
    };
    const f = nextStatusAfterChange[masterStatus.masterStatusName];
    return f ? f() : STATE_UNALLOCATED;
  };

  confirmMessage = (type) => {
    let message = (
      <p>
        By clicking OK, your changes will be saved and a driver reassignment for this trip will be needed.
      </p>
    );

    if (this.isNotYetAtGarage()) {
      message = (
        <ul>
          <li>The customer will receive a confirmation email.</li>
          <li>The driver will be unallocated.</li>
          <li>The current garage will remain allocated and won&apos;t receive any notification.</li>
        </ul>
      );

      const nextStats = this.getNextState(type);

      if (nextStats === STATE_UNALLOCATED) {
        message = (
          <ul>
            <li>The customer will receive a confirmation email.</li>
            <li>The driver will be unallocated.</li>
            <li>
              <b>&#9888;The garage will be unallocated</b> and will receive a cancellation email.
            </li>
          </ul>
        );
      }

      if (nextStats === STATE_AWAITING_PICKUP && type !== 'delivery') {
        message = (
          <ul>
            <li>The customer will receive a confirmation email.</li>
            <li>
              The driver will stay allocated and will receive a notification about the new time through
              Tookan.
            </li>
            <li>The current garage will remain allocated and won&apos;t receive any notification.</li>
          </ul>
        );
      }
    }

    if (this.keepTripsForFixterHero()) {
      message = (
        <p>
          The driver will stay allocated and will receive a notification about the new time through Tookan.
        </p>
      );
    }

    return message;
  };

  confirmDateTimeEdit = (type) => {
    const { collectionData, deliveryData } = this.state;

    const bookingEditTemplate = {
      deliveryDateTimeLocal: deliveryData.newTime,
    };

    if (this.isNotYetAtGarage()) {
      bookingEditTemplate.collectionDateTimeLocal = collectionData.newTime;
      bookingEditTemplate.masterStatusId = this.getNextState(type) || undefined;

      if (this.getNextState(type) === STATE_UNALLOCATED) {
        bookingEditTemplate.allocationStatus = 'unallocated';
      }
    }

    if (type === 'collection') {
      delete bookingEditTemplate.deliveryDateTimeLocal;
    } else {
      delete bookingEditTemplate.collectionDateTimeLocal;
    }

    this.props.patchBooking(bookingEditTemplate);

    this.resetEditingStates();
    this.resetConfirmingStates();
  };

  disabledDate = (current) => current && current < moment().subtract(1, 'days').endOf('day');

  // COLLECTION DATE TIME EDIT
  confirmCollectionTimeModal = () => {
    const { confirmLoading, confirmingCollectionTime, collectionData } = this.state;
    const title = `Update collection time to: ${collectionData.newTime}`;
    return (
      <Modal
        title={title}
        visible={confirmingCollectionTime}
        onOk={() => this.confirmDateTimeEdit('collection')}
        confirmLoading={confirmLoading}
        onCancel={this.handleCancel}
      >
        <Col>
          <div style={{ color: 'red' }}>{this.confirmMessage('collection')}</div>
        </Col>
      </Modal>
    );
  };

  onCollectionDateTimeOk = (value) => {
    this.resetEditingStates();
    this.setState({
      confirmingCollectionTime: true,
      collectionData: { newTime: moment(value).format(dateTimeFormat) },
    });
  };

  collectionDateTimePicker = () => {
    const { collectionData } = this.state;
    return (
      <DatePicker
        size="small"
        showTime={{ defultValue: moment(collectionData.time, 'HH:mm') }}
        disabledDate={this.disabledDate}
        disabledTime={this.disabledDateTime}
        format={dateTimeFormatPlaceHolder}
        placeholder={moment(collectionData.time).format(dateTimeFormatPlaceHolder)}
        onOk={this.onCollectionDateTimeOk}
      />
    );
  };

  // DELIVERY DATE TIME EDIT
  confirmDeliveryTimeModal = () => {
    const { confirmLoading, confirmingDeliveryTime, deliveryData } = this.state;
    const title = `Update delivery time to: ${deliveryData.newTime}`;
    return (
      <Modal
        title={title}
        visible={confirmingDeliveryTime}
        onOk={() => this.confirmDateTimeEdit('delivery')}
        confirmLoading={confirmLoading}
        onCancel={this.handleCancel}
      >
        <Col>
          <div style={{ color: 'red' }}>{this.confirmMessage('delivery')}</div>
        </Col>
      </Modal>
    );
  };

  onDeliveryDateTimeOk = (value) => {
    this.resetEditingStates();
    this.setState({
      confirmingDeliveryTime: true,
      deliveryData: { newTime: moment(value).format(dateTimeFormat) },
    });
  };

  deliveryDateTimePicker = () => {
    const { deliveryData } = this.state;
    return (
      <DatePicker
        size="small"
        showTime={{ defultValue: moment(deliveryData.time, 'HH:mm') }}
        disabledDate={this.disabledDate}
        disabledTime={this.disabledDateTime}
        format={dateTimeFormatPlaceHolder}
        placeholder={moment(deliveryData.time).format(dateTimeFormatPlaceHolder)}
        onOk={this.onDeliveryDateTimeOk}
        confirmLoading={this.confirmLoading}
      />
    );
  };

  /**
   * EDIT COLLECTION/DELIVERY ADDRESSES
   */

  confirmAddressEdit = (collection) => {
    const { collectionData, deliveryData } = this.state;
    this.setState({
      confirmLoading: true,
    });
    const segment = this.props.booking.segment;
    const template = {
      customerId: this.props.booking.customerId,
    };
    if (collection === 'collection') {
      template.collectionOrDelivery = 'collection';
      template.newAddress = {
        ...prepareAddressForSave(collectionData.newAddress, segment),
        segment,
      };
    } else {
      template.collectionOrDelivery = 'delivery';
      template.newAddress = {
        ...prepareAddressForSave(deliveryData.newAddress, segment),
        segment,
      };
    }

    api
      .post(`/customers/${template.customerId}/addresses`, template.newAddress)
      .then(({ data }) => {
        message.success('Success');
        const patch = {};

        if (template.collectionOrDelivery === 'collection') patch.collectionAddressId = data.addressId;
        else patch.deliveryAddressId = data.addressId;

        this.props.patchBooking(patch);
        this.resetEditingStates();
        this.resetConfirmingStates();
      })
      .catch((error) => {
        message.error(get(error, 'response.data.message', error.message));
        this.resetConfirmingStates();
      });
  };

  // COLLECTION ADDRESS EDIT
  confirmCollectionAddressModal = () => {
    const { confirmLoading, confirmingCollectionAddress, collectionData } = this.state;
    return (
      <Modal
        title="Update collection address"
        visible={confirmingCollectionAddress}
        onOk={() => this.confirmAddressEdit('collection')}
        confirmLoading={confirmLoading}
        onCancel={this.handleCancel}
      >
        <Col>
          <div>Confirm address: {collectionData.newAddress.display}</div>
        </Col>
      </Modal>
    );
  };

  onSelectCollectionAddress = (value) => {
    if (!value) return;
    // Apply editing reset and confirming state in one setState so that no intermediate
    // render happens with collectionData.newAddress nulled while the confirm modal is open.
    this.setState({
      editingCollectionTime: false,
      editingDeliveryTime: false,
      editingCollectionAddress: false,
      editingDeliveryAddress: false,
      confirmingCollectionAddress: true,
      collectionData: { newAddress: value },
      deliveryData: { newTime: null, newAddress: null },
    });
  };

  formatAddress = (address) => {
    const { line1, line2, city, postcode } = address;
    return `${line1} ${line2}, ${city}, ${postcode}`;
  };

  collectionAddressEdit = () => {
    const { segment } = this.props.booking;
    if (shouldUseUkPostcodeLookup(segment)) {
      return <PostcodeLookup onSelectAddress={this.onSelectCollectionAddress} />;
    }
    const countryRule = getCountry(segment)?.toLowerCase();
    return (
      <AddressSelectorWithGooglePlaces
        searchInputId="find-collectionAddress"
        countryRule={[countryRule]}
        address={this.formatAddress(this.props.booking.collectionAddress)}
        apiKey={`${config.gMaps.apiKey}`}
        onAddressSelected={this.onSelectCollectionAddress}
      />
    );
  };

  // DELIVERY ADDRESS EDIT
  confirmDeliveryAddressModal = () => {
    const { confirmLoading, confirmingDeliveryAddress, deliveryData } = this.state;
    return (
      <Modal
        title="Update delivery address"
        visible={confirmingDeliveryAddress}
        onOk={() => this.confirmAddressEdit('delivery')}
        confirmLoading={confirmLoading}
        onCancel={this.handleCancel}
      >
        <Col>
          <div>Confirm address: {deliveryData.newAddress.display}</div>
        </Col>
      </Modal>
    );
  };

  onSelectDeliveryAddress = (value) => {
    if (!value) return;
    this.setState({
      editingCollectionTime: false,
      editingDeliveryTime: false,
      editingCollectionAddress: false,
      editingDeliveryAddress: false,
      confirmingDeliveryAddress: true,
      collectionData: { newTime: null, newAddress: null },
      deliveryData: { newAddress: value },
    });
  };

  deliveryAddressEdit = () => {
    const { segment } = this.props.booking;
    if (shouldUseUkPostcodeLookup(segment)) {
      return <PostcodeLookup onSelectAddress={this.onSelectDeliveryAddress} />;
    }
    const countryRule = getCountry(segment)?.toLowerCase();
    return (
      <AddressSelectorWithGooglePlaces
        searchInputId="find-deliveryAddress"
        countryRule={[countryRule]}
        address={this.formatAddress(this.props.booking.deliveryAddress)}
        apiKey={`${config.gMaps.apiKey}`}
        onAddressSelected={this.onSelectDeliveryAddress}
      />
    );
  };

  renderTime = (time) => (
    <DateTimeSpan>
      <b>{moment(time).format('hh:mma')}</b> {moment(time).format('ddd, Do MMM')}
    </DateTimeSpan>
  );

  render() {
    const { booking } = this.props;
    const {
      collectionDeliveryOption,
      collectionDateTimeLocal,
      deliveryDateTimeLocal,
      collectionAddress,
      deliveryAddress,
      collectionTimeSlot,
      garageInvites,
    } = booking;

    const {
      editingCollectionTime,
      editingDeliveryTime,
      confirmingCollectionTime,
      confirmingDeliveryTime,
      editingCollectionAddress,
      editingDeliveryAddress,
      confirmingCollectionAddress,
      confirmingDeliveryAddress,
    } = this.state;

    const garageInvite = booking.garageInvites.find((invite) =>
      ['accepted', 'pending', 'draft'].includes(invite.status)
    );
    const garage = get(garageInvite, 'garage', null);

    const displayedAddress = (address) =>
      address.county ? (
        <Address>
          {getAddressDisplay(address)} <br />
          {address.county} <br />
          {address.postcode}
          {address === collectionAddress
            ? this.isCollectionEditable() && this.editToggleSwitch('collection-address')
            : this.isDeliveryEditable() && this.editToggleSwitch('delivery-address')}
        </Address>
      ) : (
        <Address>
          {getAddressDisplay(address)} <br />
          {address.postcode}
          {address === collectionAddress
            ? this.isCollectionEditable() && this.editToggleSwitch('collection-address')
            : this.isDeliveryEditable() && this.editToggleSwitch('delivery-address')}
        </Address>
      );

    return (
      <DetailsCard>
        <Title>
          Collection &amp; delivery:{' '}
          {collectionDeliveryOption === 'CustomerC&D'
            ? 'customer'
            : `${collectionDeliveryOption} (${collectionTimeSlot || 30} mins)`}
        </Title>

        <RescheduleTriggerModal booking={booking} garageInvites={garageInvites} />

        <RescheduleProposalAlert booking={booking} />

        <div className="row">
          {collectionDeliveryOption === 'CustomerC&D' ? (
            <>
              <div className="col-md-6">
                <div>Drop-off at garage</div>
                {editingCollectionTime
                  ? this.collectionDateTimePicker()
                  : this.renderTime(collectionDateTimeLocal)}
                {this.isCollectionEditable() && this.editToggleSwitch('collection-time')}
                {confirmingCollectionTime ? this.confirmCollectionTimeModal() : null}

                <br />

                <div>Return date</div>
                {editingDeliveryTime ? this.deliveryDateTimePicker() : this.renderTime(deliveryDateTimeLocal)}
                {this.isDeliveryEditable() && this.editToggleSwitch('delivery-time')}
                {confirmingDeliveryTime ? this.confirmDeliveryTimeModal() : null}
              </div>

              <div className="col-md-6">
                <p className="garage-address">Garage address</p>
                {displayedGarageAddress(garage)}
                <p className="garage-address">Customer address</p>
                {editingCollectionAddress
                  ? this.collectionAddressEdit()
                  : displayedAddress(collectionAddress)}
                {confirmingCollectionAddress && this.state.collectionData.newAddress
                  ? this.confirmCollectionAddressModal()
                  : null}
              </div>
            </>
          ) : (
            <>
              <div className="col-md-6">
                <div>Collection</div>
                {editingCollectionTime
                  ? this.collectionDateTimePicker()
                  : this.renderTime(collectionDateTimeLocal)}
                {this.isCollectionEditable() && this.editToggleSwitch('collection-time')}
                {confirmingCollectionTime ? this.confirmCollectionTimeModal() : null}
                <br />
                {editingCollectionAddress
                  ? this.collectionAddressEdit()
                  : displayedAddress(collectionAddress)}
                {confirmingCollectionAddress && this.state.collectionData.newAddress
                  ? this.confirmCollectionAddressModal()
                  : null}
              </div>
              <div className="col-md-6">
                <div>Delivery</div>
                {editingDeliveryTime ? this.deliveryDateTimePicker() : this.renderTime(deliveryDateTimeLocal)}
                {this.isDeliveryEditable() && this.editToggleSwitch('delivery-time')}
                {confirmingDeliveryTime ? this.confirmDeliveryTimeModal() : null}
                <br />
                {editingDeliveryAddress ? this.deliveryAddressEdit() : displayedAddress(deliveryAddress)}
                {confirmingDeliveryAddress && this.state.deliveryData.newAddress
                  ? this.confirmDeliveryAddressModal()
                  : null}
              </div>
            </>
          )}
        </div>
      </DetailsCard>
    );
  }
}

export default BookingDetails;
